import {
  ConnectionListResult,
  GridSortProps,
  PaginatedQueryWrapper,
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import {
  FlowDownstreamSheetsFilteredByFlowOutputFieldQuery,
  FlowDownstreamSheetsFilteredByFlowOutputFieldQuery_flows_outputFieldsConnection_nodes_downstreamSheetsConnection_nodes,
  FlowDownstreamSheetsFilteredByFlowOutputFieldQueryVariables,
} from '../../../gen-src/queries/FlowDownstreamSheetsFilteredByFlowOutputFieldQuery';
import {
  FlowDownstreamSheetsQuery,
  FlowDownstreamSheetsQuery_flows_downstreamSheetsConnection_nodes,
  FlowDownstreamSheetsQueryVariables,
} from '../../../gen-src/queries/FlowDownstreamSheetsQuery';
import { SheetSortOrder } from '../../../gen-src/queries/globalTypes';
import {
  FlowDownstreamSheetsFilteredByFlowOutputFieldQueryGql,
} from '../../queries/FlowDownstreamSheetsFilteredByFlowOutputFieldQueryGql';
import { FlowDownstreamSheetsQueryGql } from '../../queries/FlowDownstreamSheetsQueryGql';
import { getRowDataForSheet, SheetsDisplayGrid, SheetsDisplayGridRowData } from '../sharedDisplayGrid/SheetsDisplayGrid';
import { SheetsValidSortFields } from '../sorting/sortProviders/SheetsGridSortProvider';

type QueryVariables = FlowDownstreamSheetsQueryVariables;
type QueryResults = FlowDownstreamSheetsQuery;
type QueryResultListNode = FlowDownstreamSheetsQuery_flows_downstreamSheetsConnection_nodes;
type GridRowData = SheetsDisplayGridRowData;

type QueryVariablesWithFilter = FlowDownstreamSheetsFilteredByFlowOutputFieldQueryVariables;
type QueryResultsWithFilter = FlowDownstreamSheetsFilteredByFlowOutputFieldQuery;
type QueryResultListNodeWithFilter =
  FlowDownstreamSheetsFilteredByFlowOutputFieldQuery_flows_outputFieldsConnection_nodes_downstreamSheetsConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  return flow ? flow.downstreamSheetsConnection : null;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  const flowOutputFields = flow && flow.outputFieldsConnection && flow.outputFieldsConnection.nodes;
  const field = flowOutputFields && flowOutputFields[0];
  return field ? field.downstreamSheetsConnection : null;
}

export type FlowDownstreamSheetsQueryGridProps =
  GridSortProps<SheetsValidSortFields> & {
    readonly flowVizportalUrlId: string;
    readonly filteredFlowOutputFieldId: string | null;
    readonly rowCount: number | null;
  };

export function FlowDownstreamSheetsQueryGrid(props: FlowDownstreamSheetsQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyDownstreamSheetsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredSheetsLineageGridMessage();

  return props.filteredFlowOutputFieldId ? (
    // Query with Selected Field as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={FlowDownstreamSheetsFilteredByFlowOutputFieldQueryGql}
      partialQueryVariables={{
        flowVizportalUrlId: props.flowVizportalUrlId,
        selectedFlowOutputFieldId: props.filteredFlowOutputFieldId,
        orderBy: props.orderBy as SheetSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForSheet}
    >
      {({ rowStore }) => <SheetsDisplayGrid
        rowStore={rowStore}
        emptyGridMessage={emptyFilteredGridMessage}
        rowCount={props.rowCount}
        orderBy={props.orderBy}
        updateSortOrder={props.updateSortOrder}
      />}
    </PaginatedQueryWrapper>
  ) : (
      // Query without Selection
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={FlowDownstreamSheetsQueryGql}
        partialQueryVariables={{
          flowVizportalUrlId: props.flowVizportalUrlId,
          orderBy: props.orderBy as SheetSortOrder,
        }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForSheet}
      >
        {({ rowStore }) => <SheetsDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    );
}
